import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  AccessibilityInfo,
  findNodeHandle,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Svg, { Path } from 'react-native-svg';

import {
  getCardPosition,
  getCardWidth,
  getCutoutPath,
  getScrimBands,
  type SpotlightPlacement,
  type SpotlightRect,
} from './spotlightGeometry';

import { Pressable } from '@/components/ui/pressable';

export type SpotlightCardProps = {
  /** Which side of the highlight the card sits on, so a caret can face the target. */
  placement: SpotlightPlacement;
  /** Attach to the card's text. It takes screen reader focus each time a step shows. */
  focusRef: RefObject<unknown>;
};

type SpotlightOverlayProps = {
  /** The highlight, already padded, in window coordinates. */
  hole: SpotlightRect;
  radius: number;
  backdropColor: string;
  /** The card's distance from the window's sides. */
  cardGutter: number;
  /** Changes with every step, so each step's card is measured and announced afresh. */
  stepKey: string;
  /** A tap on the scrim, the iOS escape gesture, and Android's back button. Omit to ignore them. */
  onDismiss?: () => void;
  renderCard: (props: SpotlightCardProps) => ReactNode;
};

/** The native props reach the DOM on web, where React warns about them, so web takes `aria-hidden`. */
const HIDDEN_FROM_SCREEN_READERS = isWeb
  ? ({ 'aria-hidden': true } as const)
  : ({
      accessible: false,
      accessibilityElementsHidden: true,
      importantForAccessibility: 'no-hide-descendants',
    } as const);

/** Leaves an editing field alone on web, where taking focus would close the reader's keyboard. */
function isEditing(): boolean {
  const active = typeof document === 'undefined' ? null : document.activeElement;

  return active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
}

/**
 * Moves a screen reader to the card, so a step is announced where it appears.
 * A web element takes focus only with a tab index; minus one keeps it out of
 * the tab order.
 */
function focusCard(node: unknown) {
  if (!node) {
    return;
  }

  if (isWeb) {
    if (isEditing()) {
      return;
    }

    const element = node as HTMLElement;

    element.setAttribute?.('tabindex', '-1');

    element.focus?.({ preventScroll: true });

    return;
  }

  const handle = findNodeHandle(node as never);

  if (handle) {
    AccessibilityInfo.setAccessibilityFocus(handle);
  }
}

/**
 * The scrim, the highlight cut out of it, and the card beside it, sized to the
 * window. Draw it inside a `SpotlightLayer`, whose origin is the window's, so
 * it lines up with targets measured in window coordinates.
 *
 * The scrim is drawn once as an even-odd SVG path and takes touches through
 * four bands around the highlight, so a tap outside it never reaches the
 * screen beneath while the highlighted target stays usable.
 *
 * Each step's card is laid out unseen first, then placed from its measured
 * height and shown.
 *
 * Nothing here animates, so there is no motion to reduce. A Reanimated
 * opacity inside iOS's full-window overlay was not applied on some mounts,
 * which left the layer invisible while its scrim still swallowed every tap.
 * Do not add a fade back without checking it on a device across several
 * mounts, and fail visible rather than transparent.
 */
export function SpotlightOverlay({
  hole,
  radius,
  backdropColor,
  cardGutter,
  stepKey,
  onDismiss,
  renderCard,
}: SpotlightOverlayProps) {
  const window = useWindowDimensions();

  const insets = useSafeAreaInsets();

  const focusRef = useRef<unknown>(null);

  const [measured, setMeasured] = useState<{ stepKey: string; height: number } | null>(null);

  const cardWidth = getCardWidth(window.width, cardGutter);

  const isMeasured = measured?.stepKey === stepKey;

  const position = getCardPosition({
    target: hole,
    card: { width: cardWidth, height: isMeasured ? measured.height : 0 },
    window,
    safeArea: { top: insets.top, bottom: insets.bottom },
    gutter: cardGutter,
  });

  useEffect(() => {
    if (isMeasured) {
      focusCard(focusRef.current);
    }
  }, [isMeasured, stepKey]);

  return (
    <View
      style={StyleSheet.absoluteFill}
      pointerEvents="box-none"
      onAccessibilityEscape={onDismiss}>
      <View style={StyleSheet.absoluteFill} pointerEvents="none">
        <Svg
          width={window.width}
          height={window.height}
          pointerEvents="none"
          {...HIDDEN_FROM_SCREEN_READERS}>
          <Path d={getCutoutPath(window, hole, radius)} fill={backdropColor} fillRule="evenodd" />
        </Svg>
      </View>

      {Object.entries(getScrimBands(window, hole)).map(([side, band]) => (
        <Pressable
          key={side}
          onPress={onDismiss}
          style={{
            position: 'absolute',
            left: band.x,
            top: band.y,
            width: band.width,
            height: band.height,
          }}
          className="web:cursor-default"
          // Hidden from screen readers, so never a Tab stop either: a focusable
          // element nobody can perceive would let Enter dismiss the tour unseen.
          focusable={false}
          {...HIDDEN_FROM_SCREEN_READERS}
        />
      ))}

      <View
        pointerEvents="box-none"
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;

          setMeasured((current) =>
            current?.stepKey === stepKey && current.height === height
              ? current
              : { stepKey, height }
          );
        }}
        style={{
          position: 'absolute',
          top: position.top,
          left: position.left,
          width: cardWidth,
          opacity: isMeasured ? 1 : 0,
          zIndex: 1,
        }}>
        {renderCard({ placement: position.placement, focusRef })}
      </View>
    </View>
  );
}
